import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  styled,
  Tab,
  Tabs,
  Badge,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Microsoft as MicrosoftIcon,
  Google as GoogleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Email as EmailIcon,
  Lock as LockIcon,
  CloudSync as CloudSyncIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Computer as ComputerIcon,
} from '@mui/icons-material';
import { IntegrationService, AvailableIntegration } from '../../services/api/integration';
import IMAPConnectionDialog from './IMAPConnectionDialog';
import SetupInstructions from '../Integrations/SetupInstructions';
import EmailSyncStatus from './EmailSyncStatus';
import { useAuthStore } from '../../store/authStore';

interface Integration {
  id: string;
  provider: string;
  status: 'connected' | 'disconnected' | 'error';
  email?: string;
  connectedAt?: Date;
  lastSync?: Date;
  scopes?: string[];
  email_sync_status?: string;
  last_email_sync_at?: Date;
}

const IntegrationCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  '&:hover': {
    borderColor: theme.palette.primary.main,
  },
}));

const ProviderIcon = styled('div')(({ theme }) => ({
  width: 48,
  height: 48,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: theme.shape.borderRadius,
  marginRight: theme.spacing(2),
}));

const StatusChip = styled(Chip)<{ status: string }>(({ theme, status }) => ({
  ...(status === 'connected' && {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  }),
  ...(status === 'disconnected' && {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.text.secondary,
  }),
  ...(status === 'error' && {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  }),
}));

const IntegrationsSettings: React.FC = () => {
  const { user } = useAuthStore();
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [availableIntegrations, setAvailableIntegrations] = useState<AvailableIntegration[]>([]);
  const [subscriptionInfo, setSubscriptionInfo] = useState<{ tier: string; status: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncProgress, setSyncProgress] = useState<Record<string, any>>({});
  const [tabValue, setTabValue] = useState(0);
  const [imapConnections, setImapConnections] = useState<any[]>([]);
  const [imapDialogOpen, setImapDialogOpen] = useState(false);
  const [editingConnection, setEditingConnection] = useState<any>(null);

  const integrationService = useMemo(() => new IntegrationService(), []);
  const isAdmin = user?.organizationRole === 'admin' || user?.organizationRole === 'owner';

  useEffect(() => {
    let mounted = true;
    const controller = new AbortController();
    
    const load = async () => {
      if (!mounted) return;
      
      try {
        setLoading(true);
        setError(null);
        
        // Load user's current integrations
        const integrationsData = await integrationService.getIntegrations();
        if (!mounted) return;
        setIntegrations(integrationsData);
        
        // Load available integrations with subscription info
        const availableData = await integrationService.getAvailableIntegrations();
        if (!mounted) return;
        setAvailableIntegrations(availableData.integrations);
        setSubscriptionInfo({
          tier: availableData.subscriptionTier,
          status: availableData.subscriptionStatus,
        });
      } catch (err: any) {
        if (!mounted) return;
        if (err.name === 'AbortError') return;
        
        console.error('Failed to load integrations:', err);
        
        // Handle rate limit error specifically
        if (err.response?.status === 429) {
          setError('Too many requests. Please wait a moment and try again.');
        } else {
          setError('Failed to load integrations');
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    
    // Delay initial load to avoid React StrictMode double mount issues
    const timer = setTimeout(load, 100);
    
    return () => {
      mounted = false;
      controller.abort();
      clearTimeout(timer);
    };
  }, [integrationService]);

  const loadIMAPConnections = useCallback(async () => {
    try {
      const response = await integrationService.getIMAPConnections();
      setImapConnections(response.connections || []);
    } catch (err) {
      console.error('Failed to load IMAP connections:', err);
    }
  }, [integrationService]);

  useEffect(() => {
    if (tabValue === 1) {  // IMAP/SMTP is now on tab index 1
      loadIMAPConnections();
    }
  }, [tabValue, loadIMAPConnections]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Load user's current integrations
      const integrationsData = await integrationService.getIntegrations();
      setIntegrations(integrationsData);
      
      // Load available integrations with subscription info
      const availableData = await integrationService.getAvailableIntegrations();
      setAvailableIntegrations(availableData.integrations);
      setSubscriptionInfo({
        tier: availableData.subscriptionTier,
        status: availableData.subscriptionStatus,
      });
    } catch (err: any) {
      console.error('Failed to load integrations:', err);
      
      // Handle rate limit error specifically
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError('Failed to load integrations');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (provider: string) => {
    try {
      setConnecting(provider);
      setError(null);
      
      // Get OAuth URL from backend
      console.log(`[IntegrationsSettings] Getting OAuth URL for ${provider}...`);
      const oauthResponse = await integrationService.getOAuthUrl(provider);
      console.log(`[IntegrationsSettings] OAuth response:`, oauthResponse);
      const { authUrl } = oauthResponse;
      console.log(`[IntegrationsSettings] Opening OAuth URL:`, authUrl);
      
      // Open OAuth popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        authUrl,
        `${provider}_oauth`,
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`
      );
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        if (popup?.closed) {
          clearInterval(pollInterval);
          setConnecting(null);
          // Reload integrations to check if connection was successful
          await loadData();
        }
      }, 1000);
      
    } catch (err: any) {
      console.error(`Failed to connect ${provider}:`, err);
      // Handle different error response structures
      let errorMessage = `Failed to connect ${provider}`;
      if (err.response?.data?.error) {
        if (typeof err.response.data.error === 'string') {
          errorMessage = err.response.data.error;
        } else if (err.response.data.error.message) {
          errorMessage = err.response.data.error.message;
        }
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      }
      setError(errorMessage);
      setConnecting(null);
    }
  };

  const handleDisconnect = async (provider: string) => {
    try {
      setError(null);
      await integrationService.disconnectIntegration(provider);
      await loadData();
    } catch (err) {
      console.error(`Failed to disconnect ${provider}:`, err);
      setError(`Failed to disconnect ${provider}`);
    }
  };

  const handleSyncEmails = async (integrationId: string) => {
    try {
      setSyncing(integrationId);
      setError(null);
      await integrationService.syncEmailsToKnowledgeGraph(integrationId);
      // Show success message
      await loadData();
    } catch (err) {
      console.error('Failed to sync emails:', err);
      setError('Failed to sync emails');
    } finally {
      setSyncing(null);
    }
  };

  const handleAddIMAPConnection = () => {
    setEditingConnection(null);
    setImapDialogOpen(true);
  };

  const handleEditIMAPConnection = (connection: any) => {
    setEditingConnection(connection);
    setImapDialogOpen(true);
  };

  const handleDeleteIMAPConnection = async (connectionId: string) => {
    if (!window.confirm('Are you sure you want to delete this connection?')) {
      return;
    }

    try {
      await integrationService.deleteIMAPConnection(connectionId);
      await loadIMAPConnections();
    } catch (err) {
      console.error('Failed to delete IMAP connection:', err);
      setError('Failed to delete connection');
    }
  };

  const handleSyncIMAPEmails = async (connectionId: string) => {
    try {
      setSyncing(connectionId);
      setError(null);
      
      // Use the async endpoint that returns immediately
      const response = await integrationService.syncIMAPEmails(connectionId);
      
      // Show success message that sync has started
      if (response.success && response.jobId) {
        // Start polling for progress using the jobId
        pollSyncStatus(connectionId, response.jobId);
      } else if (response.success) {
        // Fallback to polling without jobId
        pollSyncStatus(connectionId);
      }
      
      await loadIMAPConnections();
    } catch (err) {
      console.error('Failed to start IMAP email sync:', err);
      setError('Failed to start email sync');
      setSyncing(null);
    }
  };

  // Poll for sync status
  const pollSyncStatus = async (connectionId: string, jobId?: string) => {
    let pollCount = 0;
    const maxPolls = 300; // Poll for up to 5 minutes
    
    const pollInterval = setInterval(async () => {
      try {
        pollCount++;
        const status = await integrationService.getIMAPSyncStatus(connectionId, jobId);
        
        // Update sync progress
        setSyncProgress(prev => ({
          ...prev,
          [connectionId]: status.progress
        }));
        
        // Check if sync is complete or failed
        if (status.status === 'success' || status.status === 'failed' || status.status === 'idle' || status.status === 'completed' || pollCount >= maxPolls) {
          clearInterval(pollInterval);
          setSyncing(null);
          setSyncProgress(prev => {
            const newProgress = { ...prev };
            delete newProgress[connectionId];
            return newProgress;
          });
          await loadIMAPConnections();
        }
      } catch (err) {
        console.error('Failed to get sync status:', err);
        clearInterval(pollInterval);
        setSyncing(null);
      }
    }, 2000); // Poll every 2 seconds
  };

  const handleIMAPDialogClose = () => {
    setImapDialogOpen(false);
    setEditingConnection(null);
  };

  const handleIMAPDialogSuccess = () => {
    setImapDialogOpen(false);
    setEditingConnection(null);
    loadIMAPConnections();
  };

  const getIntegrationConfig = (provider: string) => {
    const availableIntegration = availableIntegrations.find(i => i.type === provider);
    
    switch (provider) {
      case 'microsoft_exchange':
        return {
          name: 'Microsoft Exchange Email',
          icon: <MicrosoftIcon style={{ fontSize: 32 }} />,
          iconBg: '#0078D4',
          description: 'Connect your Microsoft Exchange account to sync emails only. Perfect for accessing your work email without full Microsoft 365 permissions.',
          scopes: ['Email'],
          type: 'email_only',
          available: availableIntegration?.access_status === 'available',
        };
      case 'gmail':
        return {
          name: 'Gmail',
          icon: <GoogleIcon style={{ fontSize: 32 }} />,
          iconBg: '#EA4335',
          description: 'Connect your Gmail account to sync emails only. Access your personal or work Gmail without full Google Workspace permissions.',
          scopes: ['Email'],
          type: 'email_only',
          available: availableIntegration?.access_status === 'available',
        };
      case 'microsoft':
        return {
          name: 'Microsoft 365 (Full)',
          icon: <MicrosoftIcon style={{ fontSize: 32 }} />,
          iconBg: '#0078D4',
          description: 'Connect your Microsoft 365 account to sync emails, calendar events, and documents from Outlook, OneDrive, and Teams.',
          scopes: ['Email', 'Calendar', 'Files', 'Teams'],
          type: 'full',
          available: availableIntegration?.access_status === 'available',
          requiredTier: availableIntegration?.required_tier_name,
          requiredPrice: availableIntegration?.required_tier_price,
        };
      case 'google':
        return {
          name: 'Google Workspace (Full)',
          icon: <GoogleIcon style={{ fontSize: 32 }} />,
          iconBg: '#4285F4',
          description: 'Connect your Google Workspace account to sync emails, calendar events, and documents from Gmail, Google Calendar, and Google Drive.',
          scopes: ['Gmail', 'Calendar', 'Drive', 'Meet'],
          type: 'full',
          available: availableIntegration?.access_status === 'available',
          requiredTier: availableIntegration?.required_tier_name,
          requiredPrice: availableIntegration?.required_tier_price,
        };
      default:
        return null;
    }
  };

  const renderIntegrationCard = (provider: string) => {
    const config = getIntegrationConfig(provider);
    if (!config) return null;

    const integration = integrations.find(i => i.provider === provider);
    const isConnected = integration?.status === 'connected';
    const isConnecting = connecting === provider;

    return (
      <IntegrationCard key={provider}>
        <CardContent>
          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
            <ProviderIcon style={{ backgroundColor: config.iconBg, color: 'white' }}>
              {config.icon}
            </ProviderIcon>

            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Typography variant="h6">{config.name}</Typography>
                  {config.type === 'email_only' && (
                    <Chip
                      size="small"
                      icon={<EmailIcon />}
                      label="Email Only"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </div>
                <StatusChip
                  size="small"
                  status={integration?.status || 'disconnected'}
                  icon={
                    isConnected ? <CheckCircleIcon /> : 
                    integration?.status === 'error' ? <CancelIcon /> : 
                    undefined
                  }
                  label={
                    isConnected ? 'Connected' : 
                    integration?.status === 'error' ? 'Error' : 
                    'Not Connected'
                  }
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <Typography variant="body2" color="text.secondary" style={{ flex: 1, marginBottom: 16 }}>
                  {config.description}
                </Typography>
                <SetupInstructions integrationType={provider} />
              </div>
              
              {isConnected && integration && (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Typography variant="body2" color="text.secondary">
                      Connected as: <strong>{integration.email}</strong>
                    </Typography>
                    {integration.lastSync && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Last synced: {new Date(integration.lastSync).toLocaleString()}
                      </Typography>
                    )}
                  </div>
                  
                  {/* Show EmailSyncStatus component for email-only integrations */}
                  {config.type === 'email_only' && (
                    <EmailSyncStatus 
                      integrationId={integration.id}
                      provider={provider}
                      onSyncTrigger={() => handleSyncEmails(integration.id)}
                    />
                  )}
                </>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                {config.scopes.map(scope => (
                  <Chip
                    key={scope}
                    label={scope}
                    size="small"
                    variant="outlined"
                    color={isConnected ? 'primary' : 'default'}
                  />
                ))}
              </div>

              {!config.available && (
                <Alert severity="info" style={{ marginBottom: 16 }}>
                  <Typography variant="body2">
                    Upgrade to {config.requiredTier} (${config.requiredPrice}/month) to access this integration.
                  </Typography>
                </Alert>
              )}
              
              {config.available && !isConnected && ['microsoft_exchange', 'gmail', 'microsoft', 'google'].includes(provider) && (
                <Alert severity="info" style={{ marginBottom: 16 }}>
                  <Typography variant="body2">
                    <strong>No credentials needed!</strong> Just click Connect to authorize access using your organization's OAuth app.
                    {['microsoft_exchange', 'gmail'].includes(provider) && (
                      <> If OAuth is not configured, you can use IMAP/SMTP connections as an alternative (see the IMAP/SMTP tab).</>
                    )}
                  </Typography>
                </Alert>
              )}
              
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {!isConnected ? (
                  <Button
                    variant="contained"
                    onClick={() => handleConnect(provider)}
                    disabled={isConnecting || !config.available}
                    startIcon={
                      isConnecting ? <CircularProgress size={16} /> : 
                      !config.available ? <LockIcon /> : null
                    }
                  >
                    {isConnecting ? 'Connecting...' : 
                     !config.available ? 'Upgrade Required' : 'Connect'}
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => handleDisconnect(provider)}
                  >
                    Disconnect
                  </Button>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </IntegrationCard>
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </div>
    );
  }

  const emailOnlyIntegrations = ['microsoft_exchange', 'gmail'];

  return (
    <div>
      <Typography variant="h6" gutterBottom>
        External Integrations
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Connect your external accounts to sync emails, calendar events, documents, and more.
      </Typography>
      
      {subscriptionInfo && (
        <Alert severity="info" style={{ marginBottom: 16 }}>
          <Typography variant="body2">
            Your subscription: <strong>{subscriptionInfo.tier}</strong>
          </Typography>
        </Alert>
      )}
      
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
          {error}
          {error.includes('has not configured') && (
            <div style={{ marginTop: 8 }}>
              <Typography variant="body2">
                <strong>What to do:</strong>
                <br />
                {isAdmin ? (
                  <>• Go to Settings → Organization tab to configure OAuth credentials for {error.match(/configured (\w+)/)?.[1] || 'this provider'}</>
                ) : (
                  <>• Contact your organization administrator to set up OAuth credentials</>
                )}
                <br />
                • Or use IMAP/SMTP connections as an alternative (see the IMAP/SMTP tab)
              </Typography>
            </div>
          )}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={handleTabChange} style={{ marginBottom: 24 }}>
        <Tab label="Email Integrations" />
        {/* <Tab label="Full Integrations" /> */}
        <Tab label="IMAP/SMTP Connections" icon={<Badge badgeContent={imapConnections.length} color="primary" />} />
      </Tabs>

      {tabValue === 0 && (
        <div>
          <Alert severity="success" style={{ marginBottom: 24 }}>
            <Typography variant="body2">
              Email-only integrations are available to all subscription tiers. Connect to sync your emails
              to the knowledge graph for enhanced AI assistance.
            </Typography>
          </Alert>

          {emailOnlyIntegrations.map(provider => renderIntegrationCard(provider))}
        </div>
      )}

      {/* {tabValue === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <InfoIcon sx={{ mr: 1 }} />
              <Typography variant="body2">
                Full integrations provide access to email, calendar, files, and more.
                Available with Professional subscription and above.
              </Typography>
            </div>
          </Alert>

          {fullIntegrations.map(provider => renderIntegrationCard(provider))}
        </div>
      )} */}

      {tabValue === 1 && (
        <div>
          <Alert severity="info" style={{ marginBottom: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <ComputerIcon style={{ marginRight: 8 }} />
              <div style={{ flex: 1 }}>
                <Typography variant="body2">
                  Connect directly to any email server using IMAP/SMTP protocols.
                  No OAuth configuration required - just your email credentials.
                </Typography>
              </div>
              <SetupInstructions integrationType="imap" />
            </div>
          </Alert>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Typography variant="h6">IMAP/SMTP Connections</Typography>
              {syncing && (
                <Chip
                  icon={<CircularProgress size={16} />}
                  label="Sync in Progress"
                  color="primary"
                  variant="filled"
                />
              )}
            </div>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddIMAPConnection}
            >
              Add Connection
            </Button>
          </div>
          
          {imapConnections.length === 0 ? (
            <Card>
              <CardContent>
                <Typography variant="body1" color="text.secondary" align="center">
                  No IMAP connections configured yet. Click "Add Connection" to get started.
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <List>
              {imapConnections.map((connection) => {
                const progress = syncProgress[connection.id];
                const isSyncing = syncing === connection.id;
                
                return (
                  <Card key={connection.id} style={{ marginBottom: 16 }}>
                    <ListItem>
                      <ListItemText
                        primary={connection.connection_name}
                        secondary={
                          <>
                            <Typography component="span" variant="body2">
                              {connection.email_address} • {connection.imap_host}
                            </Typography>
                            {connection.last_sync_at && (
                              <Typography component="span" variant="caption" display="block">
                                Last synced: {new Date(connection.last_sync_at).toLocaleString()}
                              </Typography>
                            )}
                            
                            {/* Permanent email processing statistics */}
                            <div style={{ marginTop: 8, padding: 8, borderRadius: 4 }}>
                              <Typography component="span" variant="caption" color="primary" display="block">
                                📊 <strong>Emails in Knowledge Graph: {connection.sync_stats?.totalInKG || 0}</strong>
                              </Typography>
                              
                              {connection.sync_stats?.lastSyncProcessed > 0 && (
                                <Typography component="span" variant="caption" display="block" color="text.secondary">
                                  Last sync: Processed {connection.sync_stats.lastSyncProcessed} emails, 
                                  Added {connection.sync_stats.lastSyncAdded} new
                                </Typography>
                              )}
                              
                              {/* Show current sync progress if syncing */}
                              {isSyncing && (
                                <div style={{ marginTop: 8 }}>
                                  <Typography variant="caption" fontWeight="bold" color="primary" display="block">
                                    🔄 Sync Stage: <strong>{progress?.currentStage || 'Initializing...'}</strong>
                                  </Typography>
                                  
                                  {progress?.emailsFetched !== undefined && progress.emailsFetched > 0 && (
                                    <Typography variant="caption" color="primary" display="block">
                                      ✓ Fetched: <strong>{progress.emailsFetched}</strong> emails
                                    </Typography>
                                  )}
                                  
                                  {progress?.emailsProcessed !== undefined && progress.emailsProcessed > 0 && (
                                    <Typography variant="caption" color="primary" display="block">
                                      ✓ Processed: <strong>{progress.emailsProcessed}</strong> / 
                                      Added to KG: <strong>{progress.emailsAddedToKG || 0}</strong>
                                    </Typography>
                                  )}
                                  
                                  <LinearProgress
                                    style={{ marginTop: 4, height: 6, borderRadius: 4 }}
                                    variant={progress ? "determinate" : "indeterminate"}
                                    value={progress && progress.emailsFetched > 0 
                                      ? (progress.emailsProcessed / progress.emailsFetched) * 100 
                                      : 0}
                                  />
                                </div>
                              )}
                            </div>
                          </>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title={isSyncing ? "Syncing emails..." : "Sync emails to knowledge graph"}>
                          <span>
                            <IconButton
                              edge="end"
                              onClick={() => handleSyncIMAPEmails(connection.id)}
                              disabled={isSyncing}
                            >
                              {isSyncing ? (
                                <CircularProgress size={24} />
                              ) : (
                                <CloudSyncIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isSyncing ? "Cannot edit while syncing" : "Edit connection"}>
                          <span>
                            <IconButton
                              edge="end"
                              onClick={() => handleEditIMAPConnection(connection)}
                              disabled={isSyncing}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={isSyncing ? "Cannot delete while syncing" : "Delete connection"}>
                          <span>
                            <IconButton
                              edge="end"
                              onClick={() => handleDeleteIMAPConnection(connection.id)}
                              disabled={isSyncing}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  </Card>
                );
              })}
            </List>
          )}
        </div>
      )}
      
      <Divider style={{ marginTop: 24, marginBottom: 24 }} />
      
      <Typography variant="body2" color="text.secondary">
        Your data is securely encrypted and only accessible by you.
      </Typography>
      
      <IMAPConnectionDialog
        open={imapDialogOpen}
        onClose={handleIMAPDialogClose}
        onSuccess={handleIMAPDialogSuccess}
        editConnection={editingConnection}
      />
    </div>
  );
};

export default IntegrationsSettings;